<template>
  <ElForm ref="form" :model="form" :rules="rules" hide-required-asterisk class="multi-page-form">
    <div v-for="(field, key, index) in fields" :key="index" class="multi-page-form__field">
      <ElFormItem
        v-if="!field.type || Object.values(FieldTypes).includes(field.type)"
        :prop="key"
        :label="$t(key)"
        :class="{ uploader: field.type === FieldTypes.FILE }"
      >
        <ElInput
          v-if="!field.type || field.type === FieldTypes.TEXT"
          v-model="form[key]"
          :placeholder="field.example || ''"
        />

        <ElInputNumber
          v-else-if="field.type === FieldTypes.NUMBER"
          v-model="form[key]"
          :placeholder="field.example || ''"
          :controls="false"
          class="multi-page-form__input"
        />

        <ElDatePicker
          v-else-if="field.type === FieldTypes.DATE_TIME"
          v-model="form[key]"
          type="datetime"
          class="multi-page-form__datepicker"
        >
        </ElDatePicker>

        <ElDatePicker
          v-else-if="field.type === FieldTypes.DATE"
          v-model="form[key]"
          type="date"
          class="multi-page-form__datepicker"
        >
        </ElDatePicker>

        <div
          v-else-if="field.type === FieldTypes.FILE"
          class="multi-page-form__upload"
          :class="{ max: !!form[key] && !!form[key].length }"
        >
          <ElUpload
            class="upload-demo"
            drag
            action="#"
            :ref="'uploader' + index"
            :file-list="fileLists[key]"
            :limit="2"
            :auto-upload="false"
            :accept="getFileType(field)"
            :on-change="(file, fileList) => onSuccessUploadFile(file, fileList, key, index)"
            :on-remove="() => handleRemove(key, index)"
          >
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">
              <div class="el-upload__text-header" v-html="$t('common__uploader_text')"></div>
              <div class="el-upload__text-footer">
                <span>{{ getFileType(field, true) }} {{ $t('common__files') }}</span>
              </div>
            </div>
          </ElUpload>
        </div>
      </ElFormItem>
    </div>
  </ElForm>
</template>

<script>
import ElForm from 'element-ui/lib/form';
import ElFormItem from 'element-ui/lib/form-item';
import ElInput from 'element-ui/lib/input';
import ElInputNumber from 'element-ui/lib/input-number';
import ElUpload from 'element-ui/lib/upload';
import ElDatePicker from 'element-ui/lib/date-picker';
import 'element-ui/lib/theme-chalk/index.css';
import {
  simpleRequiredValidationRule,
  fileValidationRule,
  regexpTextValidationRule,
} from '../rules';
import cloneDeep from 'lodash/cloneDeep';
import dayjs from 'dayjs';
import { RegexpFormatter } from '@/helpers/regexFormatter';

const FieldTypes = {
  TEXT: 'text',
  NUMBER: 'number',
  DATE: 'date',
  DATE_TIME: 'datetime',
  FILE: 'file',
};

const availableFileType = [
  'jpg',
  'png',
  'gif',
  'txt',
  'csv',
  'xls',
  'xlsx',
  'doc',
  'docx',
  'rtf',
  'pdf',
];

export default {
  components: {
    ElForm,
    ElFormItem,
    ElInput,
    ElUpload,
    ElInputNumber,
    ElDatePicker,
  },
  props: {
    fields: {
      type: Object,
      required: true,
    },
    method: {
      type: String,
      required: true,
    },
  },

  watch: {
    // Required to dynamically change the translation of error messages in the form
    '$i18n.locale'() {
      if (!this.formValid) {
        this.submit();
      }
    },
  },

  created() {
    this.initForm();
  },

  data() {
    return {
      form: {},
      rules: {},
      fileLists: {},
      formValid: true,
      FieldTypes,
    };
  },

  methods: {
    // This method returns the object with the form, and or the valide form
    async submit() {
      return this.$refs.form
        .validate()
        .then(() => {
          this.formValid = true;
          return {
            valid: true,
            form: this.prepatedData(),
          };
        })
        .catch(() => {
          this.formValid = false;

          return {
            valid: false,
            form: null,
          };
        });
    },

    prepatedData() {
      const data = cloneDeep(this.form);

      Object.keys(this.fields).forEach(key => {
        if (
          this.fields[key].type === FieldTypes.DATE ||
          this.fields[key].type === FieldTypes.DATE_TIME
        ) {
          data[key] = dayjs(data[key]).format();
        }
      });

      return data;
    },

    initForm() {
      Object.keys(this.fields).forEach(key => {
        this.$set(this.form, key, '');
        if (
          !this.fields[key].type ||
          this.fields[key].type === FieldTypes.TEXT ||
          this.fields[key].type === FieldTypes.NUMBER
        ) {
          this.$set(
            this.rules,
            key,
            regexpTextValidationRule(
              'blur',
              {
                expression: new RegexpFormatter().parse(this.fields[key].regexp, {
                  sub_string: /\\\\/g,
                  new_string: '\\',
                }),
              },
              this.fields[key].required,
              this
            )
          );
        }

        if (
          this.fields[key].type === FieldTypes.DATE ||
          this.fields[key].type === FieldTypes.DATE_TIME
        ) {
          this.form[key] = new Date();

          this.$set(
            this.rules,
            key,
            simpleRequiredValidationRule(this.fields[key].required, 'blur', this)
          );
        }

        if (this.fields[key].type === FieldTypes.FILE) {
          this.fileLists[key] = [];
          this.$set(
            this.rules,
            key,
            fileValidationRule(
              'change',
              this.fields[key].file_size,
              this.fields[key].required,
              this
            )
          );
        }
      });
    },

    handleRemove(key, index) {
      this.form[key] = null;
      const uploader = 'uploader' + index;
      this.$refs[uploader][0].clearFiles();
    },

    async onSuccessUploadFile(file, fileList, key, index) {
      if (!file || !file.raw) {
        return;
      }

      // This check is necessary to control file types.
      // Since there is a bug in the ElUploader component,
      // the component loads files when they do not pass by type.
      const acceptfileTypes = this.getFileType(this.fields[key]);
      const fileType = file.name.split('.').pop();

      if (!acceptfileTypes.includes(fileType)) {
        this.handleRemove(key, index);
        return;
      }

      if (fileList.length > 1) {
        await this.overrideFileList(fileList.slice(-1), key);
      }

      var reader = new FileReader();
      reader.readAsDataURL(file.raw);

      reader.onload = () => {
        this.form[key] = reader.result;
      };

      reader.onerror = error => {
        // TODO: add handler for error
        console.error(error);
      };
    },

    overrideFileList(files, key) {
      return new Promise(res => {
        this.fileLists[key] = [];
        //Timeout required for animation perform
        this.fileLists[key] = files.map(f => ({ name: f.name, url: URL.createObjectURL(f.raw) }));
        res();
      });
    },

    getFileType(field, forLabel = false) {
      const types =
        field.file_extensions && field.file_extensions.length
          ? field.file_extensions
          : availableFileType;

      return types.map(el => `.${el}`).join(forLabel ? '/' : ',');
    },
  },
};
</script>

<style lang="scss">
.multi-page-form {
  margin-top: 32px;

  .el-form-item {
    &.uploader .el-form-item__content {
      margin: unset;
    }
  }

  &__input {
    width: 100%;

    input {
      text-align: left !important;
    }
  }

  &__datepicker {
    width: 100% !important;

    input {
      padding: 10px 30px !important;
    }
  }

  &__upload {
    width: 100%;
    height: 115px;

    &.max {
      height: 150px;
    }

    .upload-demo {
      position: relative;
    }

    .upload-demo {
      width: 100%;
      height: 100%;
    }

    .el-upload,
    .el-upload-dragger {
      height: 90px;
      width: 100%;
    }

    .el-upload-list {
      position: absolute;
      bottom: 0;
      width: 100%;
    }

    .el-upload-dragger {
      display: flex;
      flex-direction: column;
      justify-content: center;

      i {
        font-size: 38px;
        line-height: 38px;
        margin: 0 0 5px;

        @media (max-width: 470px) {
          font-size: 28px;
          line-height: 28px;
          margin: 0;
        }
      }
      .el-upload__text {
        line-height: 17px;

        @media (max-width: 470px) {
          line-height: 13px;

          &-header {
            font-size: 10px;
          }

          &-footer {
            font-size: 9px !important;
          }
        }

        &-footer {
          font-size: 10px;
        }

        em {
          color: #0020ff;
        }
      }

      &:hover {
        border-color: #0020ff;
      }
    }
  }

  .el-form-item {
    &__content {
      width: 100%;
      margin-bottom: 24px;
      position: relative;
    }

    &.is-error {
      .el-input__inner {
        border-color: #e82a41;
      }
    }

    &.is-required {
      .el-form-item__label {
        &::after {
          content: '*';
          color: #e82a41;
          font-weight: 400;
          font-size: 14px;
          line-height: 20px;
          margin-left: 4px;
        }
      }
    }

    &__error {
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      color: #e82a41;
      position: absolute;
      display: flex;
      width: 100%;
      bottom: -24px;
      left: 0;
      justify-content: left;
      align-items: center;

      &::before {
        content: '';
        width: 16px;
        height: 13px;
        background-image: url('../assets/error.svg');
        margin-right: 8px;
        margin-bottom: 2px;
      }
    }

    &__label {
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 0.5px;
      color: #7f8fa4;
      margin-bottom: 6px;
      float: left;
    }
  }

  .el-input__inner {
    width: 100%;
    padding: 10px 16px;
    border: 1px solid #e1e8ee;
    border-radius: 6px;
    color: #334150;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.5px;
    outline: unset;
    transition: border-color 0.3s;

    &::placeholder {
      color: #cdd5db;
    }

    &:hover {
      border-color: #cdd5db;
    }

    &:focus {
      border-color: #0020ff;
    }
  }
}
</style>
